import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Bot, Send, ArrowLeft, Lock, GraduationCap, Target, RotateCcw, BadgeCheck, Globe, BookOpen, FileText, ChevronDown, AlertTriangle, Paperclip, X, Maximize2, Image as ImageIcon, ThumbsUp, ThumbsDown, Check, FlaskConical } from 'lucide-react';
import SEO from '../components/SEO';
import { logger } from '../lib/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'agente_wing_sessao';
const META_MARKER = '<<<WING_META>>>';

const BADGE_CONFIG = {
  official_behringer: { icon: BadgeCheck, label: 'Confirmado no manual oficial', cls: 'bg-green-500/15 text-green-400 border-green-500/30' },
  wing_academy_derived_official: { icon: GraduationCap, label: 'Orientação WING Academy', cls: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30' },
  wing_academy_institutional: { icon: BookOpen, label: 'WING Academy Brasil', cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  live_web: { icon: Globe, label: 'Informação atual da web', cls: 'bg-blue-500/15 text-blue-400 border-blue-500/30' },
  external_verified: { icon: BookOpen, label: 'Contexto técnico externo', cls: 'bg-purple-500/15 text-purple-400 border-purple-500/30' },
  wing_advanced_nonofficial: { icon: FlaskConical, label: 'Base de Engenharia (não oficial)', cls: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30' },
};

const mdComponents = {
  p: (props) => <p className="mb-2 last:mb-0 leading-relaxed" {...props} />,
  ul: (props) => <ul className="list-disc pl-5 space-y-1 mb-2" {...props} />,
  ol: (props) => <ol className="list-decimal pl-5 space-y-1 mb-2" {...props} />,
  strong: (props) => <strong className="text-white font-bold" {...props} />,
  a: (props) => <a className="text-yellow-400 underline underline-offset-2" {...props} />,
  table: (props) => <div className="overflow-x-auto my-2"><table className="w-full text-xs border border-gray-700 rounded" {...props} /></div>,
  thead: (props) => <thead className="bg-gray-800 text-yellow-400" {...props} />,
  th: (props) => <th className="text-left px-2 py-1 border-b border-gray-700 font-bold" {...props} />,
  td: (props) => <td className="px-2 py-1 border-b border-gray-800 text-gray-300" {...props} />,
  code: (props) => <code className="bg-gray-800 text-yellow-300 px-1 py-0.5 rounded text-xs" {...props} />,
};

const MSG_BOAS_VINDAS = 'Olá! Sou o **Agente Técnico da WING Academy Brasil**, treinado com o manual oficial da Behringer WING.\n\nPosso te ajudar com dúvidas sobre **roteamento, sources, buses, efeitos, firmware, gravação** e as diferenças entre **WING Full, Compact e Rack**.\n\nQual é a sua dúvida?';

const AgenteBadges = ({ meta }) => {
  if (!meta) return null;
  const badges = meta.badges || [];
  const insuf = meta.evidence_status === 'INSUFFICIENT_EVIDENCE';
  if (badges.length === 0 && !insuf) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-3" data-testid="agente-badges">
      {insuf && (
        <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border bg-amber-500/15 text-amber-400 border-amber-500/30" data-testid="badge-insuficiente">
          <AlertTriangle className="w-3 h-3" /> Sem confirmação oficial
        </span>
      )}
      {badges.map((b) => {
        const cfg = BADGE_CONFIG[b];
        if (!cfg) return null;
        const Icon = cfg.icon;
        return (
          <span key={b} className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${cfg.cls}`} data-testid={`badge-${b}`}>
            <Icon className="w-3 h-3" /> {cfg.label}
          </span>
        );
      })}
    </div>
  );
};

const AgenteTelas = ({ telas, onAmpliar }) => {
  if (!telas || telas.length === 0) return null;
  return (
    <div className="mt-3 space-y-2" data-testid="agente-telas">
      <div className="flex items-center gap-1.5 text-xs text-yellow-400 font-semibold">
        <ImageIcon className="w-3.5 h-3.5" />
        {telas.length > 1 ? 'Telas da console (manual oficial)' : 'Tela da console (manual oficial)'}
      </div>
      <div className={`grid gap-2 ${telas.length > 1 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'}`}>
        {telas.map((t) => (
          <button
            key={t.id}
            onClick={() => onAmpliar?.(`${BACKEND_URL}/api/agente/screen/${t.id}`)}
            data-testid={`tela-${t.id}`}
            className="group relative block text-left bg-black/40 border border-gray-800 hover:border-yellow-500 rounded-lg overflow-hidden transition-colors"
            title="Ampliar tela"
          >
            <img
              src={`${BACKEND_URL}/api/agente/screen/${t.id}`}
              alt={t.titulo}
              loading="lazy"
              className="w-full object-contain bg-black"
            />
            <span className="absolute top-2 right-2 flex items-center justify-center w-7 h-7 rounded-full bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity">
              <Maximize2 className="w-3.5 h-3.5 text-yellow-400" />
            </span>
            <div className="px-2.5 py-1.5 text-xs font-medium text-gray-200 border-t border-gray-800">{t.titulo}</div>
          </button>
        ))}
      </div>
    </div>
  );
};

const AgenteFontes = ({ citations, onAmpliar }) => {
  const [aberto, setAberto] = useState(false);
  if (!citations || citations.length === 0) return null;
  const ehWeb = citations.some((c) => c.source_type === 'live_web' || c.url);
  return (
    <div className="mt-2 border-t border-gray-800 pt-2">
      <button
        onClick={() => setAberto((v) => !v)}
        data-testid="agente-ver-fonte-btn"
        className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-yellow-400 transition-colors"
      >
        {ehWeb ? <Globe className="w-3.5 h-3.5" /> : <FileText className="w-3.5 h-3.5" />}
        {ehWeb ? `Fontes na web (${citations.length})` : `Ver fonte no manual (${citations.length})`}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${aberto ? 'rotate-180' : ''}`} />
      </button>
      {aberto && (
        <div className="mt-2 space-y-2" data-testid="agente-fontes-lista">
          {citations.map((c) => (
            c.url ? (
              <a
                key={c.ref}
                href={c.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`fonte-web-${c.ref}`}
                className="flex items-center gap-2 text-xs bg-black/40 border border-gray-800 hover:border-blue-500/50 rounded-lg p-2.5 transition-colors"
              >
                <Globe className="w-4 h-4 text-blue-400 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-blue-300 font-semibold truncate">[{c.ref}] {c.dominio || c.documento}</div>
                  <div className="text-gray-500">{c.rotulo}{c.data_consulta ? ` · consulta ${c.data_consulta}` : ''}</div>
                </div>
              </a>
            ) : (
              <div key={c.ref} className="text-xs bg-black/40 border border-gray-800 rounded-lg p-2.5 flex gap-3" data-testid={`fonte-${c.ref}`}>
                {c.pagina_imagem && (
                  <button
                    onClick={() => onAmpliar?.(`${BACKEND_URL}/api/rag/image/${c.pagina_imagem}`)}
                    className="relative group flex-shrink-0"
                    data-testid={`fonte-img-${c.ref}`}
                    title="Ampliar página do manual"
                  >
                    <img
                      src={`${BACKEND_URL}/api/rag/image/${c.pagina_imagem}`}
                      alt={`Página ${c.pagina} do ${c.documento}`}
                      loading="lazy"
                      className="w-16 h-20 object-cover object-top rounded border border-gray-700 group-hover:border-yellow-500 transition-colors"
                    />
                    <span className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded">
                      <Maximize2 className="w-4 h-4 text-yellow-400" />
                    </span>
                  </button>
                )}
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="font-bold text-white">[{c.ref}] {c.documento}</span>
                    {c.versao && <span className="text-gray-500">v {c.versao}</span>}
                  </div>
                  <div className="text-gray-400">
                    {c.secao && <span>Seção: <span className="text-gray-300">{c.secao}</span> · </span>}
                    <span>Página(s): <span className="text-gray-300">{c.pagina}</span></span>
                  </div>
                  {c.trecho && <p className="text-gray-500 italic mt-1 leading-snug">"{c.trecho}"</p>}
                </div>
              </div>
            )
          ))}
        </div>
      )}
    </div>
  );
};

const GateForm = ({ onAcesso }) => {
  const [nome, setNome] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [isAluno, setIsAluno] = useState(null);
  const [erro, setErro] = useState('');
  const [enviando, setEnviando] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro('');
    if (nome.trim().length < 2) { setErro('Digite seu nome.'); return; }
    if (whatsapp.replace(/\D/g, '').length < 10) { setErro('WhatsApp inválido. Informe DDD + número.'); return; }
    if (isAluno === null) { setErro('Informe se você já é aluno da WING Academy.'); return; }
    setEnviando(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/agente/acesso`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nome: nome.trim(), whatsapp: whatsapp.trim(), is_aluno: isAluno }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Erro ao liberar acesso');
      }
      const data = await res.json();
      onAcesso(data);
    } catch (error) {
      logger.error('Erro no acesso ao agente:', error);
      setErro(error.message || 'Erro ao liberar acesso. Tente novamente.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="max-w-md mx-auto glass-dark border border-yellow-500/30 rounded-2xl p-6 sm:p-8" data-testid="agente-gate">
      <div className="w-12 h-12 sm:w-14 sm:h-14 bg-yellow-500/15 rounded-xl flex items-center justify-center mb-4 sm:mb-5">
        <Bot className="w-7 h-7 sm:w-8 sm:h-8 text-yellow-400" />
      </div>
      <h2 className="text-xl sm:text-2xl font-black text-white mb-2">Acesse o Agente Técnico</h2>
      <p className="text-gray-400 text-sm mb-6">
        Informe seu nome e WhatsApp para liberar o acesso gratuito. Você tem <strong className="text-yellow-400">15 perguntas por dia</strong>.
      </p>
      <form onSubmit={handleSubmit} className="space-y-4">
        <input
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          data-testid="agente-nome-input"
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 min-h-[48px] text-white text-base sm:text-sm focus:border-yellow-500 outline-none"
          placeholder="Seu nome"
          maxLength={100}
        />
        <input
          value={whatsapp}
          onChange={(e) => setWhatsapp(e.target.value)}
          type="tel"
          data-testid="agente-whatsapp-input"
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 min-h-[48px] text-white text-base sm:text-sm focus:border-yellow-500 outline-none"
          placeholder="WhatsApp com DDD — ex: 11 99999-9999"
          maxLength={20}
        />
        <div>
          <p className="text-gray-300 text-sm font-medium mb-2">Você já é aluno da WING Academy?</p>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setIsAluno(true)}
              data-testid="agente-aluno-sim-btn"
              className={`min-h-[48px] rounded-lg border font-bold transition-all text-base ${isAluno === true ? 'bg-yellow-500 border-yellow-500 text-black' : 'bg-gray-800 border-gray-700 text-gray-300 hover:border-yellow-500/60'}`}
            >
              Sim
            </button>
            <button
              type="button"
              onClick={() => setIsAluno(false)}
              data-testid="agente-aluno-nao-btn"
              className={`min-h-[48px] rounded-lg border font-bold transition-all text-base ${isAluno === false ? 'bg-yellow-500 border-yellow-500 text-black' : 'bg-gray-800 border-gray-700 text-gray-300 hover:border-yellow-500/60'}`}
            >
              Não
            </button>
          </div>
        </div>
        {erro && <p className="text-red-400 text-sm" data-testid="agente-gate-erro">{erro}</p>}
        <button
          type="submit"
          disabled={enviando}
          data-testid="agente-acessar-btn"
          className="w-full bg-yellow-500 hover:bg-yellow-400 active:scale-[0.99] text-black font-black py-3 min-h-[52px] rounded-lg transition-all disabled:opacity-50 text-base"
        >
          {enviando ? 'Liberando...' : 'Acessar o Agente'}
        </button>
      </form>
      <p className="text-gray-600 text-xs mt-4 flex items-start gap-1.5">
        <Lock className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
        <span>Seus dados são usados apenas para contato da WING Academy Brasil, conforme nossa <Link to="/politica-de-privacidade" className="underline text-gray-500">Política de Privacidade</Link> (LGPD).</span>
      </p>
    </div>
  );
};

export default function AgenteTecnico() {
  const [sessao, setSessao] = useState(null);
  const [mensagens, setMensagens] = useState([{ role: 'assistant', conteudo: MSG_BOAS_VINDAS }]);
  const [input, setInput] = useState('');
  const [respondendo, setRespondendo] = useState(false);
  const [limiteAtingido, setLimiteAtingido] = useState(false);
  const [anexo, setAnexo] = useState(null);
  const [anexoPreview, setAnexoPreview] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [feedbacks, setFeedbacks] = useState({});
  const fileRef = useRef(null);
  const fimRef = useRef(null);

  const enviarFeedback = async (iid, util) => {
    if (!iid || feedbacks[iid]) return;
    setFeedbacks((f) => ({ ...f, [iid]: util ? 'util' : 'nao_util' }));
    try {
      await fetch(`${BACKEND_URL}/api/agente/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ interaction_id: iid, util }),
      });
    } catch (e) {
      logger.error('Falha ao enviar feedback', e);
    }
  };

  const escolherAnexo = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(f.type)) return;
    if (f.size > 8 * 1024 * 1024) return;
    setAnexo(f);
    setAnexoPreview(URL.createObjectURL(f));
  };

  const removerAnexo = () => {
    if (anexoPreview) URL.revokeObjectURL(anexoPreview);
    setAnexo(null);
    setAnexoPreview(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  useEffect(() => {
    try {
      const salva = JSON.parse(localStorage.getItem(STORAGE_KEY));
      if (salva?.session_id) setSessao(salva);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fimRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [mensagens]);

  const handleAcesso = (data) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    setSessao(data);
    if (data.restantes <= 0) setLimiteAtingido(true);
  };

  const handleEnviar = async () => {
    const pergunta = input.trim();
    if ((!pergunta && !anexo) || respondendo || limiteAtingido) return;
    setInput('');
    setRespondendo(true);
    const anexoAtual = anexo;
    const anexoPreviewAtual = anexoPreview;
    removerAnexo();
    const textoUsuario = pergunta || (anexoAtual ? 'O que é esta tela?' : '');
    setMensagens((m) => [...m, { role: 'user', conteudo: textoUsuario, imagemUrl: anexoPreviewAtual }, { role: 'assistant', conteudo: '' }]);
    try {
      let imagemId = null;
      if (anexoAtual) {
        const fd = new FormData();
        fd.append('file', anexoAtual);
        const upRes = await fetch(`${BACKEND_URL}/api/agente/upload`, {
          method: 'POST',
          headers: { 'X-Session-Id': sessao.session_id },
          body: fd,
        });
        if (upRes.ok) {
          const upData = await upRes.json();
          imagemId = upData.image_id;
        }
      }
      const res = await fetch(`${BACKEND_URL}/api/agente/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessao.session_id, mensagem: textoUsuario, imagem_id: imagemId }),
      });
      if (res.status === 429) {
        setLimiteAtingido(true);
        setMensagens((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: 'assistant', conteudo: 'Você atingiu o **limite de 15 perguntas de hoje**. Volte amanhã — ou acelere seu aprendizado na [Mentoria em Grupo](/#inscricao)!' };
          return copy;
        });
        return;
      }
      if (res.status === 404) {
        localStorage.removeItem(STORAGE_KEY);
        setSessao(null);
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let full = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        full += decoder.decode(value, { stream: true });
        const idx = full.indexOf(META_MARKER);
        let display = full;
        let meta = null;
        if (idx !== -1) {
          display = full.slice(0, idx);
          try { meta = JSON.parse(full.slice(idx + META_MARKER.length)); } catch { /* json ainda parcial */ }
        }
        setMensagens((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: 'assistant', conteudo: display.trimEnd(), meta };
          return copy;
        });
      }
      setSessao((s) => {
        const novo = { ...s, restantes: Math.max(0, (s.restantes ?? 15) - 1) };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(novo));
        if (novo.restantes <= 0) setLimiteAtingido(true);
        return novo;
      });
    } catch (error) {
      logger.error('Erro no chat do agente:', error);
      setMensagens((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: 'assistant', conteudo: 'Desculpe, tive um problema técnico. Tente novamente em instantes.' };
        return copy;
      });
    } finally {
      setRespondendo(false);
    }
  };

  const handleSair = () => {
    localStorage.removeItem(STORAGE_KEY);
    setSessao(null);
    setMensagens([{ role: 'assistant', conteudo: MSG_BOAS_VINDAS }]);
    setLimiteAtingido(false);
  };

  return (
    <div className="min-h-screen bg-black flex flex-col">
      <SEO
        title="Agente Técnico WING — Tire Dúvidas com IA"
        description="Converse gratuitamente com o Agente Técnico da WING Academy Brasil: IA treinada com o manual oficial da Behringer WING Full, Compact e Rack."
        path="/agente"
        breadcrumbs={[{ name: 'Agente Técnico', path: '/agente' }]}
      />

      <div className="border-b border-gray-800 bg-gray-950">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" data-testid="agente-voltar-home" className="inline-flex items-center gap-2 text-gray-400 hover:text-yellow-400 transition-colors text-sm">
            <ArrowLeft className="w-4 h-4" /> WING Academy Brasil
          </Link>
          {sessao && (
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-500" data-testid="agente-restantes">
                <strong className="text-yellow-400">{sessao.restantes ?? '—'}</strong> perguntas restantes hoje
              </span>
              <button onClick={handleSair} title="Trocar usuário" data-testid="agente-sair-btn" className="text-gray-500 hover:text-white p-1">
                <RotateCcw className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="flex-1 max-w-3xl w-full mx-auto px-3 sm:px-4 py-5 sm:py-8 flex flex-col">
        <div className="text-center mb-6 sm:mb-8">
          <span className="bg-yellow-500/15 text-yellow-400 text-[11px] sm:text-xs font-bold uppercase tracking-wide px-3 py-1 rounded-full">IA • Manual Oficial Behringer</span>
          <div className="flex items-center justify-center gap-2.5 mt-3 sm:mt-4">
            <img src="/logo-wing-white.png" alt="WING Academy Brasil" className="h-8 sm:h-10 w-auto flex-shrink-0" data-testid="agente-logo" />
            <h1 className="text-lg sm:text-2xl font-black text-white whitespace-nowrap">Agente Técnico <span className="text-yellow-400">WING</span></h1>
          </div>
          <p className="text-gray-400 mt-2 text-sm sm:text-base">Tire dúvidas sobre a WING Full, Compact e Rack — respostas baseadas no manual oficial.</p>
        </div>

        {!sessao ? (
          <GateForm onAcesso={handleAcesso} />
        ) : (
          <div className="flex-1 flex flex-col glass-dark border border-gray-800 rounded-2xl overflow-hidden" data-testid="agente-chat">
            <div className="flex-1 overflow-y-auto p-3 sm:p-6 space-y-3 sm:space-y-4 min-h-[320px] max-h-[64vh] sm:max-h-[55vh]" data-testid="agente-mensagens">
              {mensagens.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  {msg.role === 'assistant' && (
                    <div className="w-8 h-8 bg-yellow-500/15 rounded-lg flex items-center justify-center mr-2 flex-shrink-0 mt-1">
                      <Bot className="w-5 h-5 text-yellow-400" />
                    </div>
                  )}
                  <div className={`max-w-[90%] sm:max-w-[85%] rounded-2xl px-3.5 py-2.5 sm:px-4 sm:py-3 text-[15px] sm:text-sm leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-yellow-500 text-black font-medium rounded-br-sm'
                      : 'bg-gray-900 border border-gray-800 text-gray-300 rounded-bl-sm'
                  }`}>
                    {msg.role === 'assistant' ? (
                      msg.conteudo ? (
                        <>
                          <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{msg.conteudo}</ReactMarkdown>
                          <AgenteBadges meta={msg.meta} />
                          <AgenteTelas telas={msg.meta?.telas} onAmpliar={setLightbox} />
                          <AgenteFontes citations={msg.meta?.citations} onAmpliar={setLightbox} />
                          {msg.meta?.interaction_id && msg.meta?.evidence_status !== 'NEEDS_CLARIFICATION' && (
                            <div className="mt-2 flex items-center gap-2" data-testid={`feedback-${msg.meta.interaction_id}`}>
                              {feedbacks[msg.meta.interaction_id] ? (
                                <span className="inline-flex items-center gap-1 text-xs text-green-400" data-testid="feedback-obrigado">
                                  <Check className="w-3.5 h-3.5" /> Obrigado pelo feedback!
                                </span>
                              ) : (
                                <>
                                  <span className="text-xs text-gray-500">Esta resposta ajudou?</span>
                                  <button
                                    onClick={() => enviarFeedback(msg.meta.interaction_id, true)}
                                    data-testid="feedback-util-btn"
                                    title="Resolveu minha dúvida"
                                    className="text-gray-400 hover:text-green-400 transition-colors p-2"
                                  >
                                    <ThumbsUp className="w-5 h-5 sm:w-4 sm:h-4" />
                                  </button>
                                  <button
                                    onClick={() => enviarFeedback(msg.meta.interaction_id, false)}
                                    data-testid="feedback-naoutil-btn"
                                    title="Não respondeu corretamente"
                                    className="text-gray-400 hover:text-red-400 transition-colors p-2"
                                  >
                                    <ThumbsDown className="w-5 h-5 sm:w-4 sm:h-4" />
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                        </>
                      ) : (
                        <span className="inline-flex gap-1 items-center text-gray-500">
                          <span className="w-1.5 h-1.5 bg-yellow-400 rounded-full animate-bounce" />
                          <span className="w-1.5 h-1.5 bg-yellow-400 rounded-full animate-bounce [animation-delay:150ms]" />
                          <span className="w-1.5 h-1.5 bg-yellow-400 rounded-full animate-bounce [animation-delay:300ms]" />
                        </span>
                      )
                    ) : (
                      <div className="space-y-2">
                        {msg.imagemUrl && (
                          <img src={msg.imagemUrl} alt="Print enviado" className="rounded-lg max-h-48 border border-black/20" data-testid="user-anexo-img" />
                        )}
                        {msg.conteudo && <span>{msg.conteudo}</span>}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              <div ref={fimRef} />
            </div>
            <div className="border-t border-gray-800 p-3 sm:p-4">
              {anexoPreview && (
                <div className="mb-2 inline-flex items-center gap-2 bg-gray-800 border border-gray-700 rounded-lg p-1.5 pr-2" data-testid="anexo-preview">
                  <img src={anexoPreview} alt="Anexo" className="w-10 h-10 object-cover rounded" />
                  <span className="text-xs text-gray-300 flex items-center gap-1"><ImageIcon className="w-3.5 h-3.5" /> Print anexado</span>
                  <button onClick={removerAnexo} data-testid="anexo-remover-btn" className="text-gray-400 hover:text-white ml-1"><X className="w-4 h-4" /></button>
                </div>
              )}
              <div className="flex gap-2">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={escolherAnexo}
                  className="hidden"
                  data-testid="agente-file-input"
                />
                <button
                  onClick={() => fileRef.current?.click()}
                  disabled={respondendo || limiteAtingido}
                  data-testid="agente-anexar-btn"
                  title="Anexar print de tela"
                  className="bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-300 hover:text-yellow-400 px-3 min-h-[48px] flex items-center justify-center rounded-lg transition-colors disabled:opacity-40 flex-shrink-0"
                >
                  <Paperclip className="w-5 h-5" />
                </button>
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleEnviar()}
                  disabled={respondendo || limiteAtingido}
                  data-testid="agente-msg-input"
                  className="flex-1 min-w-0 bg-gray-800 border border-gray-700 rounded-lg px-4 min-h-[48px] py-3 text-white focus:border-yellow-500 outline-none text-base sm:text-sm disabled:opacity-50"
                  placeholder={limiteAtingido ? 'Limite diário atingido' : 'Pergunte ou anexe um print...'}
                  maxLength={1000}
                />
                <button
                  onClick={handleEnviar}
                  disabled={respondendo || limiteAtingido || (!input.trim() && !anexo)}
                  data-testid="agente-enviar-btn"
                  className="bg-yellow-500 hover:bg-yellow-400 text-black font-bold px-4 min-h-[48px] min-w-[48px] flex items-center justify-center rounded-lg transition-colors disabled:opacity-40 flex-shrink-0"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        )}

        {lightbox && (
          <div
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
            onClick={() => setLightbox(null)}
            data-testid="agente-lightbox"
          >
            <button className="absolute top-4 right-4 text-white hover:text-yellow-400" data-testid="lightbox-fechar-btn">
              <X className="w-8 h-8" />
            </button>
            <img src={lightbox} alt="Página do manual" className="max-w-full max-h-full object-contain rounded shadow-2xl" onClick={(e) => e.stopPropagation()} />
          </div>
        )}
      </div>
    </div>
  );
}
